import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import { useDepartmentListScreen } from './hooks/useDepartmentListScreen'
import { appConfirm } from '../../../components/AppConfirmModal'



function getInitials(name) {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (words.length === 0) return 'DP'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return `${words[0][0]}${words[1][0]}`.toUpperCase()
}

function statusChipClasses(status) {
  if (status === 'Active') return 'bg-emerald-100 text-emerald-700'
  if (status === 'Pending Review' || status === 'Pending') return 'bg-amber-100 text-amber-700'
  return 'bg-surface-variant text-on-surface-variant'
}

function statusDotClasses(status) {
  if (status === 'Active') return 'bg-emerald-500'
  if (status === 'Pending Review' || status === 'Pending') return 'bg-amber-500'
  return 'bg-outline'
}

export default function DepartmentMasterListScreen() {
  const { state, actions } = useDepartmentListScreen()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-7 pb-8">
      <section className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">Organizational Structure</p>
          <h1 className="mt-2 text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">Department Master</h1>
        </div>

        <p className="max-w-xl text-right text-base leading-relaxed text-on-surface-variant">
          Configure and manage organizational units across your enterprise. Establish reporting hierarchies and
          operational domains for precise resource allocation.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_280px]">
        <article className="rounded-[2rem] border border-outline-variant/35 bg-surface-container-low p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-end">
            <label className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Department Name</span>
              <input
                type="text"
                value={state.searchText}
                onChange={(event) => actions.setSearchText(event.target.value)}
                placeholder="Filter by name..."
                className="w-full rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
              />
            </label>

            <label className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Company</span>
              <AppDropdown
                value={state.companyId}
                onChange={actions.setCompanyId}
                placeholder="All Companies"
                triggerClassName="w-full rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface"
                options={[
                  { value: '', label: 'All Companies' },
                  ...state.companyOptions.map((option) => ({
                    value: String(option.id),
                    label: option.name,
                  })),
                ]}
              />
            </label>

            <div className="grid gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Status</span>
              <div className="flex gap-1.5 rounded-xl bg-surface-container-high p-1">
                <button
                  type="button"
                  onClick={() => actions.setStatusFilter('active')}
                  className={`flex-1 rounded-lg px-3 py-2.5 text-xs font-bold transition-colors ${
                    state.statusFilter === 'active'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'bg-surface-container-lowest text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => actions.setStatusFilter('pending')}
                  className={`flex-1 rounded-lg px-3 py-2.5 text-xs font-bold transition-colors ${
                    state.statusFilter === 'pending'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'bg-surface-container-lowest text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Pending Review
                </button>
                <button
                  type="button"
                  onClick={() => actions.setStatusFilter('inactive')}
                  className={`flex-1 rounded-lg px-3 py-2.5 text-xs font-bold transition-colors ${
                    state.statusFilter === 'inactive'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'bg-surface-container-lowest text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>
        </article>

        <article className="rounded-[2rem] bg-primary p-6 text-on-primary shadow-lg shadow-primary/20">
          <div className="flex items-start justify-between">
            <span className="material-symbols-outlined text-3xl">hub</span>
            <span className="text-4xl font-black">{state.totalCount || state.rows.length}</span>
          </div>
          <p className="mt-4 text-lg font-semibold leading-tight text-white/75">Total Departments across active structure</p>
        </article>
      </section>

      {state.error ? (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      {state.success ? (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.success}</div>
      ) : null}

      <section className="overflow-hidden rounded-[2rem] border border-outline-variant/35 bg-surface-container-lowest">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1020px] text-left">
            <thead>
              <tr className="bg-surface-container-low/60 text-[11px] uppercase tracking-[0.14em] text-on-surface-variant">
                <th className="px-8 py-5 font-black">Department Name</th>
                <th className="px-8 py-5 font-black">Company Name</th>
                <th className="px-8 py-5 font-black">Status</th>
                <th className="px-8 py-5 text-right font-black">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-surface-container-low">
              {state.isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-on-surface-variant">
                    Loading departments...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-on-surface-variant">
                    No departments found for selected filters.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-surface-container-low/30">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-container text-xl font-bold text-primary">
                          {getInitials(row.name)}
                        </div>
                        <div>
                          <p className="text-lg font-bold leading-tight text-on-surface">{row.name}</p>
                          <p className="text-xs text-on-surface-variant">ID: {row.code}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-8 py-6 text-base text-on-surface">{row.companyName}</td>

                    <td className="px-8 py-6">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold ${statusChipClasses(
                          row.status,
                        )}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${statusDotClasses(row.status)}`} />
                        {row.status}
                      </span>
                    </td>

                    <td className="px-8 py-6">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/masters/department/entry?id=${row.id}`}
                          className="rounded-full p-2 text-outline transition-colors hover:bg-surface-container-high hover:text-primary"
                          title="Edit department"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </Link>

                        <button
                          type="button"
                          disabled={state.isMutating}
                          onClick={async () => {
                            const confirmed = await appConfirm({
                              title: row.isArchived ? 'Delete Department' : 'Archive Department',
                              message: row.isArchived
                                ? `Are you sure you want to permanently delete department "${row.name}"?`
                                : `Archive department "${row.name}"?`,
                              confirmText: row.isArchived ? 'Delete' : 'Archive',
                              variant: 'danger',
                            })
                            if (confirmed) actions.handleRowDeleteAction(row)
                          }}
                          className="rounded-full p-2 text-outline transition-colors hover:bg-error-container hover:text-error disabled:cursor-not-allowed disabled:opacity-60"
                          title={row.isArchived ? 'Delete department' : 'Archive department'}
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-outline-variant/30 bg-surface-container-low/30 px-8 py-5 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rangeStart} - {state.rangeEnd} of {state.totalCount || state.rows.length} departments
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}



